// Mods accumulator (refactor B.2, monorail-3it). Every player stat modifier
// folds here, once per frame (modsSystem in pipeline.ts, ai stage order -1 —
// ahead of predator-ai, the earliest consumer). Systems read player.mods;
// call-site multiplication of source accessors is the thing this replaces.
//
// Adding a source (e.g. equipment affixes, monorail-yqv): write its accessor
// in its own module, fold it into the relevant channel below. One line.
//
// Radius is deliberately absent: applyPlayerRadius (growth.ts) is its own
// synchronous composition pipeline, invoked from non-frame contexts (evolve
// purchase, engulf toggle, eating) where a one-frame-stale value would show.

import type { Entity } from './components'
import { applyItemAffixes } from './items'
import {
  autophagyRate,
  axopodDamageMult,
  axopodReachMult,
  flagellateSpeedMult,
  osmoticReachBonus,
} from './powers'
import {
  mutationAllyDamageMult,
  mutationDetectionRadiusMult,
  mutationDnaMult,
  mutationMaxHpMult,
  mutationOrbMagnetRange,
  mutationRecruitReachAdd,
  mutationSpeedMult,
} from './mutations'

export type Mods = NonNullable<Entity['mods']>

export function emptyMods(): Mods {
  return {
    speedMult: 1,
    dnaMult: 1,
    maxHpMult: 1,
    regenRate: 0,
    eatReachAdd: 0,
    recruitReachAdd: 0,
    orbMagnetRange: 0,
    allyDamageMult: 1,
    detectionRadiusMult: 1,
    attackDamageMult: 1,
    attackDamageAdd: 0,
    attackReachMult: 1,
    attackCooldownMult: 1,
    attackLifesteal: 0,
    damageTakenMult: 1,
    dashCooldownMult: 1,
    thornsDamage: 0,
  }
}

/** Fold all modifier sources into player.mods (created if missing) and apply
 * the channels that push state elsewhere (max HP). Runs every frame; also
 * safe to call ad hoc when a test or respawn wants fresh values immediately. */
export function recomputeMods(player: Entity): Mods {
  const mods = (player.mods ??= emptyMods())
  mods.speedMult = flagellateSpeedMult(player) * mutationSpeedMult(player)
  mods.dnaMult = mutationDnaMult(player)
  mods.maxHpMult = mutationMaxHpMult(player)
  mods.regenRate = autophagyRate(player)
  mods.eatReachAdd = osmoticReachBonus(player)
  mods.recruitReachAdd = mutationRecruitReachAdd(player)
  mods.orbMagnetRange = mutationOrbMagnetRange(player)
  mods.allyDamageMult = mutationAllyDamageMult(player)
  mods.detectionRadiusMult = mutationDetectionRadiusMult(player)
  mods.attackDamageMult = axopodDamageMult(player)
  mods.attackReachMult = axopodReachMult(player)
  // Item-only channels reset to identity, then equipped affixes fold on top
  // of everything (multiplicative with powers/mutations where they share a
  // channel — e.g. Cruel × Axopod).
  mods.attackDamageAdd = 0
  mods.attackCooldownMult = 1
  mods.attackLifesteal = 0
  mods.damageTakenMult = 1
  mods.dashCooldownMult = 1
  mods.thornsDamage = 0
  applyItemAffixes(player, mods)
  applyMaxHp(player, mods)
  return mods
}

// Max HP is the one channel that writes state rather than being read: the
// folded multiplier scales a cached pre-modifier baseline, and current HP
// clamps down if the ceiling dropped (losing a stack never heals you).
function applyMaxHp(player: Entity, mods: Mods): void {
  if (!player.health) return
  mods.baseMaxHp ??= player.health.max
  const newMax = Math.max(1, Math.round(mods.baseMaxHp * mods.maxHpMult))
  player.health.max = newMax
  if (player.health.current > newMax) player.health.current = newMax
}
